import styles from './styles.module.css'

import { MdContentCopy } from '@react-icons/all-files/md/MdContentCopy'
import { MdInfo } from '@react-icons/all-files/md/MdInfo'
import { MdLink } from '@react-icons/all-files/md/MdLink'
import { MdOpenInNew } from '@react-icons/all-files/md/MdOpenInNew'
import React, { useEffect, useState } from 'react'

import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import { TRACK_SHARING } from '@disco/constants/FeatureTypes'
import { AUDIO_FILE, VIDEO_FILE } from '@disco/constants/FileTypes'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { resetPlaylistShareForm } from '@disco/state-management/actions/FormActions'
import { getTrackShares } from '@disco/state-management/actions/TrackShareActions'
import { isDeviceTypePhone } from '@disco/state-management/selectors/app'
import { isFeatureEnabled } from '@disco/state-management/selectors/business'
import { getFileTypeByExt } from '@disco/utils/file-utils'
import { pushUINotification } from '@disco/utils/notifications'
import { getNestedTracksCount } from '@disco/utils/tracks'

import type { Track } from 'types/track'

import TrackInfo from 'components/common/track-info'
import {
  Box,
  Dialog,
  DiscoCallout,
  Flex,
  IconButton,
  SegmentedControl,
  Tabs,
  Text,
  TextField,
  Tooltip,
} from 'components/ds'
import { HoverProvider } from 'components/ui/hover-provider'
import QRCodeButton from 'components/ui/qr-code-button'

import { useDispatch, useSelector } from 'hooks/use-redux'

import type { RootState } from 'store/configureStore'

import { TrackAssignTab } from './track-assign-tab'
import { TrackEmailTab } from './track-email-tab'
import { TrackEmbedTab } from './track-embed-tab'

type Props = {
  track: Track
  onClose: () => void
  sharedChannel?: boolean
}

type TabValue = 'share' | 'copy-url' | 'embed'
type ShareSubTab = 'email' | 'assign'

const ASSIGN_URL_TOOLTIP =
  "If you'd rather send a track yourself instead of using DISCO's email share function, assign a URL to your contact and send it directly. You'll still be able to track their activity once they access it."

interface CopyUrlRowProps {
  label: string
  url: string
  qrFilename: string
  onCopy: () => void
  onQrAction: (action: 'download' | 'copy') => void
}

const CopyUrlRow: React.FC<CopyUrlRowProps> = ({
  label,
  url,
  qrFilename,
  onCopy,
  onQrAction,
}) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url)
      pushUINotification({ level: 'success', message: 'Link copied!' })
      onCopy()
    } catch {
      pushUINotification({ level: 'error', message: 'Failed to copy link' })
    }
  }

  return (
    <Box mb="3">
      <Text size="2" weight="medium" as="div" mb="1">
        {label}
      </Text>
      <Flex gap="2" align="center">
        <Box flexGrow="1">
          <TextField.Root
            readOnly
            size="2"
            value={url}
            onFocus={e => e.target.select()}
          />
        </Box>
        <Tooltip content="Copy">
          <IconButton
            variant="soft"
            size="2"
            disabled={!url}
            onClick={handleCopy}
            aria-label={`Copy ${label}`}
          >
            <MdContentCopy />
          </IconButton>
        </Tooltip>
        <Tooltip content="Open in new tab">
          <IconButton variant="soft" size="2" disabled={!url} asChild>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Open ${label} in new tab`}
            >
              <MdOpenInNew />
            </a>
          </IconButton>
        </Tooltip>
        <QRCodeButton
          url={url}
          filename={qrFilename}
          disabled={!url}
          onAction={onQrAction}
        />
      </Flex>
    </Box>
  )
}

/**
 * Track share modal, rebuilt on Radix `Dialog`/`Tabs` in place of the legacy
 * react-modal dialog. Hosts Share to contact (email or assign-a-URL, via
 * {@link TrackEmailTab}/{@link TrackAssignTab}), Copy URL, and Embed
 * (via {@link TrackEmbedTab}) behind a single top-level tab bar.
 */
const ShareTrackModal: React.FC<Props> = ({
  track,
  onClose,
  sharedChannel,
}) => {
  const dispatch = useDispatch()
  const isPhone = useSelector(isDeviceTypePhone)
  const disablePublicDownloads = useSelector(
    (state: RootState) =>
      state.user.business?.private_metadata.disable_public_downloads,
  )
  const isTrackSharingEnabled = useSelector(state =>
    isFeatureEnabled(state, { featureName: TRACK_SHARING }),
  )

  const fileType = getFileTypeByExt(track.ext)
  const displayEmbedTab = fileType === AUDIO_FILE || fileType === VIDEO_FILE
  const hasNestedTracks = getNestedTracksCount(track) > 0

  const [activeTab, setActiveTab] = useState<TabValue>(
    isTrackSharingEnabled ? 'share' : 'copy-url',
  )
  const [shareSubTab, setShareSubTab] = useState<ShareSubTab>('email')
  const [playUrl, setPlayUrl] = useState(track.public_play_url)
  const [streamUrl, setStreamUrl] = useState(track.stream_url)
  const [embedUrl, setEmbedUrl] = useState('')
  const [embedParams, setEmbedParams] = useState({
    download: !disablePublicDownloads,
  })

  const sharedChannelContext = sharedChannel
    ? { Context: 'Shared Channels' }
    : {}

  useEffect(() => {
    mixpanel.sendPureEvent('Open modal', {
      Modal: 'Share Track',
      'Track ID': track.id,
      Catalog: track.origin?.source_library?.id,
      'Catalog Business': track.origin?.business?.id,
      ...sharedChannelContext,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.id])

  useEffect(() => {
    dispatch(getTrackShares(track.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.id])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      // SLAM-1974: /api/shorturl/ 400s the whole batch if any URL is falsy
      const longUrls = [track.public_play_url, track.stream_url].filter(
        Boolean,
      )
      if (longUrls.length === 0) return

      try {
        const shortUrls = await api.shorturl(longUrls)
        const shortUrlFor = new Map(
          longUrls.map((longUrl, index) => [longUrl, shortUrls[index]]),
        )
        if (!cancelled) {
          setPlayUrl(shortUrlFor.get(track.public_play_url) ?? track.public_play_url)
          setStreamUrl(shortUrlFor.get(track.stream_url) ?? track.stream_url)
        }
      } catch (error) {
        dispatch(sendGenericError(error))
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.id])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const url = await api.getEmbeddedTrackUrl(track.id, embedParams)
        if (!cancelled) setEmbedUrl(url)
      } catch (error) {
        if (!cancelled) dispatch(sendGenericError(error))
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [track.id, embedParams])

  useEffect(() => {
    return () => {
      dispatch(resetPlaylistShareForm())
    }
  }, [dispatch])

  const handleEmbedParamsChange = (params: Partial<typeof embedParams>) => {
    setEmbedParams(prev => ({ ...prev, ...params }))
  }

  const handleCopyTrackUrl = () => {
    if (!playUrl) return
    navigator.clipboard
      .writeText(playUrl)
      .then(() => {
        pushUINotification({
          level: 'success',
          message: 'Copied track link to clipboard',
        })
        dispatch(sendMixpanelEvent('Track Share Modal - Copy track link'))
      })
      .catch(() => {
        pushUINotification({
          level: 'error',
          message: 'Failed to copy link to clipboard',
        })
      })
  }

  const onPlayUrlCopy = () =>
    mixpanel.sendPureEvent('Copy track url', {
      Url: playUrl,
      Modal: 'Share Track',
      'Track ID': track.id,
      Catalog: track.origin?.source_library?.id,
      'Catalog Business': track.origin?.business?.id,
      ...sharedChannelContext,
    })
  const onStreamUrlCopy = () =>
    mixpanel.sendPureEvent('Copy stream url', {
      Url: streamUrl,
      ...sharedChannelContext,
    })
  const onPlayUrlQrAction = (action: 'download' | 'copy') =>
    mixpanel.sendPureEvent('Copy track url', {
      Url: playUrl,
      Modal: 'Share Track',
      'Track ID': track.id,
      Catalog: track.origin?.source_library?.id,
      'Catalog Business': track.origin?.business?.id,
      Via: 'QR',
      Action: action,
      ...sharedChannelContext,
    })
  const onStreamUrlQrAction = (action: 'download' | 'copy') =>
    mixpanel.sendPureEvent('Copy stream url', {
      Url: streamUrl,
      Via: 'QR',
      Action: action,
      ...sharedChannelContext,
    })

  return (
    <Dialog.Root defaultOpen onOpenChange={onClose}>
      <Dialog.Content
        maxWidth="700px"
        align="start"
        className={styles.dialogContent}
        onOpenAutoFocus={e => e.preventDefault()}
      >
        <Dialog.Title>
          <Text size="6" weight="bold" mb="3" as="div">
            Share Track
          </Text>
        </Dialog.Title>

        {!isPhone && (
          <Box mb="4" p="3" className={styles.trackInfoContainer}>
            <Flex align="center" gap="2" width="100%">
              <Flex align="center" gap="2" flexGrow="1" minWidth="0">
                <Box minWidth="0">
                  <HoverProvider>
                    <TrackInfo location="explorer" track={track} />
                  </HoverProvider>
                </Box>
              </Flex>
              <Tooltip content="Copy public link">
                <IconButton
                  variant="soft"
                  size="2"
                  onClick={handleCopyTrackUrl}
                  aria-label="Copy public link"
                >
                  <MdLink />
                </IconButton>
              </Tooltip>
              <Tooltip content="Open public link in new tab">
                <IconButton variant="soft" size="2" asChild>
                  <a
                    href={playUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Open public link in new tab"
                  >
                    <MdOpenInNew />
                  </a>
                </IconButton>
              </Tooltip>
            </Flex>
          </Box>
        )}

        <Tabs.Root
          value={activeTab}
          onValueChange={value => setActiveTab(value as TabValue)}
          className={styles.tabBody}
        >
          <Tabs.List className={styles.tabsList}>
            {isTrackSharingEnabled && (
              <Tabs.Trigger value="share">Share to contact</Tabs.Trigger>
            )}
            <Tabs.Trigger value="copy-url">Copy URL</Tabs.Trigger>
            {displayEmbedTab && (
              <Tabs.Trigger value="embed">Embed</Tabs.Trigger>
            )}
          </Tabs.List>

          <Box pt="4" className={styles.tabBody}>
            {isTrackSharingEnabled && (
              <Tabs.Content value="share" className={styles.tabContent}>
                <Box mb="4">
                  <SegmentedControl.Root
                    size="2"
                    variant="classic"
                    radius="large"
                    value={shareSubTab}
                    onValueChange={value =>
                      setShareSubTab(value as ShareSubTab)
                    }
                    className={styles.fullWidth}
                  >
                    <SegmentedControl.Item
                      value="email"
                      className={styles.focusable}
                    >
                      Email track
                    </SegmentedControl.Item>
                    <SegmentedControl.Item
                      value="assign"
                      className={styles.focusable}
                    >
                      <Flex align="center" gap="1">
                        Assign a URL to a contact
                        <Tooltip content={ASSIGN_URL_TOOLTIP}>
                          <IconButton
                            variant="ghost"
                            size="1"
                            radius="full"
                            aria-label="About assigning a URL"
                          >
                            <MdInfo size={14} />
                          </IconButton>
                        </Tooltip>
                      </Flex>
                    </SegmentedControl.Item>
                  </SegmentedControl.Root>
                </Box>

                {shareSubTab === 'email' ? (
                  <TrackEmailTab
                    track={track}
                    onClose={onClose}
                    onViewShares={() => setShareSubTab('assign')}
                  />
                ) : (
                  <TrackAssignTab track={track} onClose={onClose} />
                )}
              </Tabs.Content>
            )}

            <Tabs.Content value="copy-url" className={styles.tabContent}>
              <Box className={styles.scrollArea}>
                {track.watermark && (
                  <Box mb="3">
                    <DiscoCallout type="warning">
                      Watermarked tracks downloaded from public track share
                      links will not be traceable to individual recipients.
                    </DiscoCallout>
                  </Box>
                )}
                {hasNestedTracks && (
                  <Box mb="3">
                    <DiscoCallout
                      type="info"
                      dismissible
                      dismissKey="share_nested_track"
                    >
                      You’re sharing a track with nested versions. All versions
                      will be visible on this link.
                    </DiscoCallout>
                  </Box>
                )}
                {!disablePublicDownloads && (
                  <CopyUrlRow
                    label="Track URL"
                    url={playUrl}
                    qrFilename={`${track.name || 'track'}-qr`}
                    onCopy={onPlayUrlCopy}
                    onQrAction={onPlayUrlQrAction}
                  />
                )}
                <CopyUrlRow
                  label="Streaming-only URL"
                  url={streamUrl}
                  qrFilename={`${track.name || 'track'}-stream-qr`}
                  onCopy={onStreamUrlCopy}
                  onQrAction={onStreamUrlQrAction}
                />
              </Box>
            </Tabs.Content>

            {displayEmbedTab && (
              <Tabs.Content value="embed" className={styles.tabContent}>
                <Box className={styles.scrollArea}>
                  {track.watermark && (
                    <Box mb="3">
                      <DiscoCallout type="warning">
                        Watermarked tracks downloaded from embedded players will
                        not be traceable to individual recipients.
                      </DiscoCallout>
                    </Box>
                  )}
                  {hasNestedTracks && (
                    <Box mb="3">
                      <DiscoCallout
                        type="info"
                        dismissible
                        dismissKey="embed_nested_track"
                      >
                        Only the main version of this track will appear in your
                        embed.
                      </DiscoCallout>
                    </Box>
                  )}
                  <TrackEmbedTab
                    iframeId={`disco-track-${track.id}`}
                    embedUrl={embedUrl}
                    embedParams={embedParams}
                    onParamsChange={handleEmbedParamsChange}
                    onClose={onClose}
                    disablePublicDownloads={Boolean(disablePublicDownloads)}
                  />
                </Box>
              </Tabs.Content>
            )}
          </Box>
        </Tabs.Root>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default ShareTrackModal
